import { DEFAULT_CURRENCY } from "@/config/currency.config";
import type { CurrencyValue } from "@/constants/propertyConstants";
import type { ConvertedPrices } from "@/types/pricing.types";

export interface PlansFilterParams {
  rooms?: number[];
  minPrice?: number;
  maxPrice?: number;
  areaMin?: number;
  areaMax?: number;
  sortBy?: string;
}

export interface FilterableLayout {
  _id: string;
  rooms?: number;
  area?: number;
  totalPrice?: number;
  convertedPrices?: ConvertedPrices;

  [key: string]: any;
}

export function parseLayoutsFilters(
  searchParams: URLSearchParams,
): PlansFilterParams {
  const filters: PlansFilterParams = {};

  // Parse rooms array from rooms[]=value format
  const roomsValues = searchParams.getAll("rooms[]");
  if (roomsValues.length > 0) {
    filters.rooms = roomsValues
      .map((val) => parseInt(val, 10))
      .filter((val) => !isNaN(val) && val >= 1 && val <= 10);

    if (filters.rooms.length === 0) {
      delete filters.rooms;
    }
  }

  // Parse numeric filters
  const minPrice = searchParams.get("minPrice");
  if (minPrice) {
    const parsed = parseFloat(minPrice);
    if (!isNaN(parsed) && parsed >= 0) {
      filters.minPrice = parsed;
    }
  }

  const maxPrice = searchParams.get("maxPrice");
  if (maxPrice) {
    const parsed = parseFloat(maxPrice);
    if (!isNaN(parsed) && parsed >= 0) {
      filters.maxPrice = parsed;
    }
  }

  const areaMin = searchParams.get("areaMin");
  if (areaMin) {
    const parsed = parseFloat(areaMin);
    if (!isNaN(parsed) && parsed >= 0) {
      filters.areaMin = parsed;
    }
  }

  const areaMax = searchParams.get("areaMax");
  if (areaMax) {
    const parsed = parseFloat(areaMax);

    if (!isNaN(parsed) && parsed >= 0) {
      filters.areaMax = parsed;
    }
  }

  const sortBy = searchParams.get("sortBy");
  if (sortBy) {
    const validSortOptions = [
      "price_asc",
      "price_desc",
      "rooms_asc",
      "rooms_desc",
    ];
    if (validSortOptions.includes(sortBy)) {
      filters.sortBy = sortBy;
    }
  }

  return filters;
}

const getLayoutPrice = (
  layout: FilterableLayout,
  currency: CurrencyValue,
): number | undefined => {
  const convertedPrice = layout.convertedPrices?.[currency];

  if (typeof convertedPrice === "number") {
    return convertedPrice;
  }

  if (typeof layout.totalPrice === "number") {
    return layout.totalPrice;
  }

  return undefined;
};

const resolveCurrency = (currency?: CurrencyValue): CurrencyValue => {
  if (currency) {
    return currency;
  }

  return DEFAULT_CURRENCY as CurrencyValue;
};

export function filterLayouts(
  layouts: FilterableLayout[],
  filters: PlansFilterParams,
  currency?: CurrencyValue,
): FilterableLayout[] {
  if (!layouts || layouts.length === 0) {
    return [];
  }

  const activeCurrency = resolveCurrency(currency);

  return layouts.filter((layout) => {
    const layoutPrice = getLayoutPrice(layout, activeCurrency);

    // Filter by rooms
    if (filters.rooms && filters.rooms.length > 0) {
      if (!layout.rooms || !filters.rooms.includes(layout.rooms)) {
        return false;
      }
    }

    // Filter by total price
    if (filters.minPrice !== undefined) {
      if (layoutPrice === undefined || layoutPrice < filters.minPrice) {
        return false;
      }
    }

    if (filters.maxPrice !== undefined) {
      if (layoutPrice === undefined || layoutPrice > filters.maxPrice) {
        return false;
      }
    }

    // Filter by area
    if (filters.areaMin !== undefined) {
      if (!layout.area || layout.area < filters.areaMin) {
        return false;
      }
    }

    if (filters.areaMax !== undefined) {
      if (!layout.area || layout.area > filters.areaMax) {
        return false;
      }
    }

    return true;
  });
}

export function sortLayouts(
  layouts: FilterableLayout[],
  sortBy: string = "price_asc",
  currency?: CurrencyValue,
): FilterableLayout[] {
  if (!layouts || layouts.length === 0) {
    return layouts;
  }

  const sortedLayouts = [...layouts];
  const activeCurrency = resolveCurrency(currency);

  switch (sortBy) {
    case "price_asc":
      return sortedLayouts.sort((a, b) => {
        const priceA = getLayoutPrice(a, activeCurrency) || 0;
        const priceB = getLayoutPrice(b, activeCurrency) || 0;
        return priceA - priceB;
      });

    case "price_desc":
      return sortedLayouts.sort((a, b) => {
        const priceA = getLayoutPrice(a, activeCurrency) || 0;
        const priceB = getLayoutPrice(b, activeCurrency) || 0;
        return priceB - priceA;
      });

    case "rooms_asc":
      return sortedLayouts.sort((a, b) => {
        const roomsA = a.rooms || 0;
        const roomsB = b.rooms || 0;
        return roomsA - roomsB;
      });

    case "rooms_desc":
      return sortedLayouts.sort((a, b) => {
        const roomsA = a.rooms || 0;
        const roomsB = b.rooms || 0;
        return roomsB - roomsA;
      });

    default:
      return sortedLayouts;
  }
}

export function hasActiveFilters(filters: PlansFilterParams): boolean {
  return !!(
    (filters.rooms && filters.rooms.length > 0) ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.areaMin !== undefined ||
    filters.areaMax !== undefined ||
    filters.sortBy !== undefined
  );
}
